import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { sendPasswordResetEmail } from "firebase/auth";
import { getRoleAuth } from "../../auth/roleSession";

const ACADEMIC_ROLES = ["Dean", "Program Head", "Professor", "Vice President"];
const SERVICES_ROLES = ["Registrar", "Admission Counselor", "Room Manager", "Finance"];
const SUPPORTED_ROLES = [
  ...ACADEMIC_ROLES,
  ...SERVICES_ROLES,
  "System Administrator",
  "New Student",
];

const LOGIN_PATHS = {
  Dean: "/academics",
  "Program Head": "/academics",
  Professor: "/academics",
  "Vice President": "/academics",
  Registrar: "/registar-login",
  "Admission Counselor": "/admission-login",
  "Room Manager": "/room-management-login",
  Finance: "/finances",
  "System Administrator": "/admin-login",
  "New Student": "/signin",
};

const RESET_CONFIRMATION = "If an account exists for this email, a password reset link has been sent. If you don't receive it, check spam or contact support.";

const ALLOWED_RETURN_PATHS = new Set([
  "/academics",
  "/services",
  "/registar-login",
  "/admission-login",
  "/room-management-login",
  "/finances",
  "/admin-login",
  "/signin",
]);

function getRoleOptions(scope) {
  if (scope === "academics") return ACADEMIC_ROLES;
  if (scope === "services") return SERVICES_ROLES;
  return SUPPORTED_ROLES.includes(scope) ? [scope] : SUPPORTED_ROLES;
}

export default function ForgotPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const scope = searchParams.get("scope") || "";
  const roleOptions = getRoleOptions(scope);
  const [role, setRole] = useState(roleOptions.length === 1 ? roleOptions[0] : "");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const requestedReturnPath = searchParams.get("returnTo");
  const returnPath = ALLOWED_RETURN_PATHS.has(requestedReturnPath)
    ? requestedReturnPath
    : LOGIN_PATHS[role] || "/";

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setMessage("");

    if (!role) {
      setError("Select the account role or service used to sign in.");
      return;
    }

    const auth = getRoleAuth(role);
    if (!auth) {
      setError("Password recovery is not available for this login.");
      return;
    }

    setLoading(true);
    try {
      const normalizedEmail = email.trim().toLowerCase();
      if (role === "New Student") {
        // Keep student reset links compatible with the existing action-code reset page.
        const response = await fetch("https://us-central1-enrollease-19c30.cloudfunctions.net/sendPasswordResetEmail", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: normalizedEmail }),
        });
        if (!response.ok) {
          throw new Error(`Student password reset request failed with status ${response.status}`);
        }
      } else {
        await sendPasswordResetEmail(auth, normalizedEmail);
      }
      setMessage(RESET_CONFIRMATION);
    } catch (resetError) {
      console.error("Password reset request failed", resetError);
      if (role === "New Student" || resetError?.code === "auth/user-not-found") {
        setMessage(RESET_CONFIRMATION);
      } else if (resetError?.code === "auth/invalid-email") {
        setError("Enter a valid email address.");
      } else {
        setError("We couldn't process your request right now. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <main style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, boxSizing: "border-box", background: "linear-gradient(90deg, #009966 0%, #155c2b 100%)" }}>
      <section style={{ width: "100%", maxWidth: 440, background: "#fff", borderRadius: 20, boxShadow: "0 6px 24px rgba(0,0,0,0.12)", padding: "32px clamp(20px, 6vw, 36px)", boxSizing: "border-box" }}>
        <h1 style={{ textAlign: "center", fontSize: 24, fontWeight: 800, color: "#155c2b", margin: "0 0 8px" }}>EnrollEase</h1>
        <h2 style={{ textAlign: "center", fontSize: 20, color: "#24322c", margin: "0 0 8px" }}>Forgot Password?</h2>
        <p style={{ textAlign: "center", color: "#666", fontSize: 14, lineHeight: 1.5, margin: "0 0 24px" }}>
          Enter your account email to request a password reset link.
        </p>

        <form onSubmit={handleSubmit}>
          {roleOptions.length > 1 && (
            <>
              <label htmlFor="forgot-password-role" style={{ display: "block", fontWeight: 600, color: "#155c2b", marginBottom: 6 }}>Login role or service</label>
              <select
                id="forgot-password-role"
                value={role}
                onChange={event => {
                  setRole(event.target.value);
                  setMessage("");
                  setError("");
                }}
                required
                style={{ width: "100%", marginBottom: 14, padding: "12px 14px", borderRadius: 8, border: "1px solid #d1d5db", boxSizing: "border-box", background: "#fff" }}
              >
                <option value="">Select role or service</option>
                {roleOptions.map(option => <option key={option} value={option}>{option === "Finance" ? "Finance (Treasurer / Financial Officer)" : option}</option>)}
              </select>
            </>
          )}

          <label htmlFor="forgot-password-email" style={{ display: "block", fontWeight: 600, color: "#155c2b", marginBottom: 6 }}>Email</label>
          <input
            id="forgot-password-email"
            type="email"
            value={email}
            onChange={event => setEmail(event.target.value)}
            placeholder="you@school.edu.ph"
            autoComplete="email"
            required
            style={{ width: "100%", marginBottom: 14, padding: "12px 14px", borderRadius: 8, border: "1px solid #d1d5db", boxSizing: "border-box" }}
          />

          {error && <div role="alert" style={{ color: "#b00020", fontSize: 14, marginBottom: 14 }}>{error}</div>}
          {message && <div role="status" style={{ color: "#155c2b", fontSize: 14, marginBottom: 14 }}>{message}</div>}

          <button
            type="submit"
            disabled={loading}
            style={{ width: "100%", padding: 12, background: loading ? "#ccc" : "#155c2b", color: "#fff", border: 0, borderRadius: 10, fontSize: 16, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer" }}
          >
            {loading ? "Sending..." : "Send reset link"}
          </button>
        </form>

        <button
          type="button"
          onClick={() => navigate(returnPath)}
          style={{ width: "100%", marginTop: 12, padding: 11, background: "#fff", color: "#155c2b", border: "1px solid #155c2b", borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: "pointer" }}
        >
          Back to sign in
        </button>
        <div style={{ textAlign: "center", marginTop: 16, fontSize: 13 }}>
          <Link to="/" style={{ color: "#155c2b" }}>Return to home</Link>
        </div>
      </section>
    </main>
  );
}
